import React, { useState } from 'react';
import { 
  FaTachometerAlt, 
  FaUsers, 
  FaPlus, 
  FaSignOutAlt,
  FaBars,
  FaTimes
} from 'react-icons/fa';

const AdminLayout = ({ children, currentView, setCurrentView, authKey, uid, adminData }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', view: 'dashboard', icon: FaTachometerAlt },
    { name: 'Dealers', view: 'dealers', icon: FaUsers },
    { name: 'Add Dealer', view: 'add-dealer', icon: FaPlus },
  ];

  const handleNavigation = (view) => {
    setCurrentView(view);
    setSidebarOpen(false);
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      // Clear OAuth session token cookie
      document.cookie = 'sessionToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      
      // Redirect to home or login page
      window.location.href = '/';
    }
  };

  const getCurrentPageTitle = () => {
    const currentPage = navigation.find(item => item.view === currentView);
    return currentPage ? currentPage.name : 'Admin Portal';
  };

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar for desktop */}
      <div className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 lg:bg-gray-800">
        <div className="flex-1 flex flex-col min-h-0">
          {/* Logo/Brand */}
          <div className="bg-gray-900 p-6">
            <h1 className="text-xl font-bold text-white">Admin Portal</h1>
            <p className="text-gray-300 text-sm mt-1">Dealer Management</p>
            {adminData?.store?.storeName && (
              <p className="text-xs text-gray-400 mt-1 truncate">
                {adminData.store.storeName}
              </p>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-2">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.view}
                  onClick={() => handleNavigation(item.view)}
                  className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-200 ${
                    currentView === item.view
                      ? 'bg-gray-700 text-white border-l-4 border-blue-500'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  <Icon className="mr-3 h-5 w-5" />
                  {item.name}
                </button>
              );
            })}
          </nav>

          {/* Admin info and logout */}
          <div className="p-4 border-t border-gray-700">
            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-blue-500 w-10 h-10 rounded-full flex items-center justify-center">
                {/* <FaCog className="text-white text-sm" /> */}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {adminData?.user?.username || 'Admin User'}
                </p>
                <p className="text-xs text-gray-300 truncate">
                  {adminData?.user?.storeEmail || adminData?.store?.storeEmail || 
                   (uid ? `UID: ${uid.substring(0, 8)}...` : 'Admin')}
                </p>
                {adminData?.user?.storeId && (
                  <p className="text-xs text-gray-400">
                    Store ID: {adminData.user.storeId}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-4 py-2 text-sm text-red-400 hover:bg-gray-700 rounded-lg transition-colors duration-200"
            >
              <FaSignOutAlt className="mr-3 h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-gray-800">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                onClick={() => setSidebarOpen(false)}
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              >
                <FaTimes className="h-6 w-6 text-white" />
              </button>
            </div>
            
            {/* Mobile sidebar content */}
            <div className="flex-1 flex flex-col min-h-0">
              <div className="bg-gray-900 p-6">
                <h1 className="text-xl font-bold text-white">Admin Portal</h1>
                <p className="text-gray-300 text-sm mt-1">Dealer Management</p>
                {adminData?.store?.storeName && (
                  <p className="text-xs text-gray-400 mt-1 truncate">
                    {adminData.store.storeName}
                  </p>
                )}
              </div>

              <nav className="flex-1 px-4 py-4 space-y-2">
                {navigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.view}
                      onClick={() => handleNavigation(item.view)}
                      className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-200 ${
                        currentView === item.view
                          ? 'bg-gray-700 text-white'
                          : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                      }`}
                    >
                      <Icon className="mr-3 h-5 w-5" />
                      {item.name}
                    </button>
                  );
                })}
              </nav>

              <div className="p-4 border-t border-gray-700">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="bg-blue-500 w-8 h-8 rounded-full flex items-center justify-center">
                    {/* <FaCog className="text-white text-xs" /> */}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">
                      {adminData?.user?.username || 'Admin User'}
                    </p>
                    <p className="text-xs text-gray-300 truncate">
                      {adminData?.user?.storeEmail || adminData?.store?.storeEmail || 'Admin'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center px-4 py-2 text-sm text-red-400 hover:bg-gray-700 rounded-lg transition-colors duration-200"
                >
                  <FaSignOutAlt className="mr-3 h-4 w-4" />
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col flex-1">
        {/* Top bar for mobile */}
        <div className="lg:hidden bg-white shadow-sm border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-gray-500 hover:text-gray-900"
          >
            <FaBars className="h-6 w-6" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900">{getCurrentPageTitle()}</h1>
          <div className="w-6"></div> {/* Spacer for alignment */}
        </div>

        {/* Page content */}
        <main className="flex-1 p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout; 